import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { Star, Quote, ThumbsUp, User, Send, Filter, Loader2, MessageSquare, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average: 0, total: 0, breakdown: {} });
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 5,
    comment: '',
    orderNumber: ''
  });

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get('/reviews');
      setReviews(response.data);
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching reviews:', error);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/reviews/stats');
      setStats(response.data);
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRatingClick = (rating) => {
    setFormData({
      ...formData,
      rating
    });
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post('/reviews', formData);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        rating: 5,
        comment: '',
        orderNumber: ''
      });

      setTimeout(() => {
        setSubmitSuccess(false);
        setShowReviewForm(false);
        fetchReviews(); // Refresh list
        fetchStats(); // Refresh stats
      }, 3000);
    } catch (error) {
      logger.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleHelpful = async (reviewId) => {
    try {
      const response = await axios.post(`/reviews/${reviewId}/helpful`);
      // Update the review in the list
      setReviews(reviews.map(review =>
        review.id === reviewId
          ? { ...review, helpful: response.data.helpful }
          : review
      ));
    } catch (error) {
      logger.error('Error marking helpful:', error);
    }
  };

  const filterReviews = () => {
    if (selectedFilter === 'all') return reviews;
    return reviews.filter(review => review.rating === parseInt(selectedFilter));
  };

  const filteredReviews = filterReviews();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-gold-500 animate-spin mx-auto mb-4" />
          <div className="text-gold-400 text-xl font-serif">Loading reviews...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 relative overflow-hidden">
      {/* Background Gradients */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-900/10 rounded-full blur-3xl opacity-20" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gold-900/10 rounded-full blur-3xl opacity-20" />
      </div>

      <div className="container mx-auto px-6 pt-40 pb-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Badge variant="outline" className="mb-4 border-gold-500 text-gold-400">
            Community Feedback
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-serif">
            Customer Reviews
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            See what our happy customers have to say about Magic World. Your feedback helps us improve.
          </p>

          <Button
            onClick={() => setShowReviewForm(!showReviewForm)}
            className={`${showReviewForm ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-gold-500 hover:bg-gold-400 text-black'} font-bold shadow-lg transition-all duration-300 transform hover:scale-105`}
          >
            {showReviewForm ? (
              <><X className="mr-2 w-4 h-4" /> Cancel Review</>
            ) : (
              <><MessageSquare className="mr-2 w-4 h-4" /> Write a Review</>
            )}
          </Button>
        </motion.div>

        {/* Review Form */}
        <AnimatePresence>
          {showReviewForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-12 overflow-hidden"
            >
              <Card className="bg-gray-900/60 border-gold-500/50 backdrop-blur-md max-w-2xl mx-auto">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-gold-500 mb-6 font-serif flex items-center gap-2">
                    <Star className="w-6 h-6 fill-gold-500" /> Share Your Experience
                  </h2>

                  {submitSuccess ? (
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="bg-green-500/10 border border-green-500 rounded-lg p-8 text-center"
                    >
                      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Send className="text-black w-8 h-8" />
                      </div>
                      <h3 className="text-2xl font-bold text-green-400 mb-2">Thank You!</h3>
                      <p className="text-gray-300">Your review has been submitted successfully.</p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmitReview} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gold-200">Your Name *</label>
                          <Input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter your name"
                            className="bg-gray-800/50 border-gray-700 focus:border-gold-500"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gold-200">Email (Optional)</label>
                          <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="your@email.com"
                            className="bg-gray-800/50 border-gray-700 focus:border-gold-500"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gold-200">Order Number (Optional)</label>
                        <Input
                          type="text"
                          name="orderNumber"
                          value={formData.orderNumber}
                          onChange={handleInputChange}
                          placeholder="e.g. ORD-12345"
                          className="bg-gray-800/50 border-gray-700 focus:border-gold-500 uppercase"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gold-200">Your Rating *</label>
                        <div className="flex justify-center p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => handleRatingClick(star)}
                                className="focus:outline-none transition-transform hover:scale-110"
                              >
                                <Star
                                  className={`w-8 h-8 ${star <= formData.rating ? 'text-gold-500 fill-gold-500' : 'text-gray-600'
                                    }`}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gold-200">Your Review *</label>
                        <textarea
                          name="comment"
                          value={formData.comment}
                          onChange={handleInputChange}
                          required
                          rows="5"
                          className="flex w-full rounded-md border border-gray-700 bg-gray-800/50 px-3 py-2 text-sm text-white placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gold-500 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                          placeholder="Tell us about the food, service, and delivery..."
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-gold-500 text-black font-bold h-12 hover:bg-gold-400"
                      >
                        {submitting ? (
                          <><Loader2 className="mr-2 w-5 h-5 animate-spin" /> Submitting...</>
                        ) : (
                          <><Send className="mr-2 w-5 h-5" /> Submit Review</>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          {/* Average Card */}
          <Card className="bg-gray-900/60 border-gray-800 backdrop-blur-sm md:col-span-1">
            <CardContent className="p-6 flex flex-col items-center justify-center h-full text-center">
              <div className="text-6xl font-bold text-gold-500 mb-2 font-serif">{stats.average || 0}</div>
              <div className="flex mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-6 h-6 ${i < Math.floor(stats.average) ? 'text-gold-500 fill-gold-500' : 'text-gray-600'}`}
                  />
                ))}
              </div>
              <p className="text-gray-400">Based on {stats.total} reviews</p>
            </CardContent>
          </Card>

          {/* Breakdown Card */}
          <Card className="bg-gray-900/60 border-gray-800 backdrop-blur-sm md:col-span-2">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Filter className="w-5 h-5 text-gold-500" /> Rating Distribution
              </h3>
              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map((star) => (
                  <div key={star} className="flex items-center gap-3">
                    <div className="flex items-center w-12 text-gold-400 font-bold">
                      {star} <Star className="w-3 h-3 ml-1 fill-gold-400" />
                    </div>
                    <div className="flex-1 bg-gray-800 rounded-full h-2.5 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: stats.total > 0 ? `${((stats.breakdown[star] || 0) / stats.total) * 100}%` : '0%' }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="bg-gold-500 h-full rounded-full"
                      />
                    </div>
                    <div className="w-8 text-right text-sm text-gray-500">{stats.breakdown[star] || 0}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Filters */}
        {reviews.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <Button
              variant={selectedFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedFilter('all')}
              className={selectedFilter === 'all' ? 'bg-gold-500 text-black hover:bg-gold-400' : 'border-gray-700 text-gray-300 hover:bg-gray-800'}
            >
              All Reviews
            </Button>
            {[5, 4, 3, 2, 1].map(rating => (
              <Button
                key={rating}
                variant={selectedFilter === rating.toString() ? 'default' : 'outline'}
                onClick={() => setSelectedFilter(rating.toString())}
                className={selectedFilter === rating.toString() ? 'bg-gold-500 text-black hover:bg-gold-400' : 'border-gray-700 text-gray-300 hover:bg-gray-800'}
              >
                {rating} Stars
              </Button>
            ))}
          </div>
        )}

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AnimatePresence>
            {filteredReviews.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="col-span-full py-12 text-center"
              >
                <div className="w-20 h-20 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-10 h-10 text-gray-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-300 mb-2">No reviews found</h3>
                <p className="text-gray-500">
                  {reviews.length === 0 ? "Be the first to share your experience!" : "Try selecting a different rating filter."}
                </p>
              </motion.div>
            ) : (
              filteredReviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="h-full"
                >
                  <Card className="h-full bg-gray-900/40 border-gray-800 hover:border-gold-500/30 transition-colors backdrop-blur-sm">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center shadow-lg">
                            <User className="text-black w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="text-white font-bold">{review.name}</h4>
                            <div className="flex items-center text-xs text-green-500 font-medium">
                              <Badge variant="secondary" className="bg-green-500/10 text-green-500 hover:bg-green-500/20 px-1 py-0 h-5 text-[10px]">VERIFIED</Badge>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex gap-0.5 mb-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3.5 h-3.5 ${i < review.rating ? 'text-gold-500 fill-gold-500' : 'text-gray-700'}`}
                              />
                            ))}
                          </div>
                          <p className="text-xs text-gray-500">{new Date(review.createdAt).toLocaleDateString()}</p>
                        </div>
                      </div>

                      <div className="relative pl-4 border-l-2 border-gold-500/20 mb-4">
                        <Quote className="absolute -top-2 -left-2 w-3 h-3 text-gold-500 opacity-50" />
                        <p className="text-gray-300 italic">{review.comment}</p>
                      </div>

                      <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-800">
                        {review.orderNumber && (
                          <Badge variant="outline" className="border-gray-700 text-gray-500 text-xs">
                            Order #{review.orderNumber}
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleHelpful(review.id)}
                          className="text-gray-400 hover:text-gold-400 hover:bg-transparent h-8 p-0"
                        >
                          <ThumbsUp className={`w-4 h-4 mr-1.5 ${review.helpful > 0 ? 'text-gold-500' : ''}`} />
                          Helpful ({review.helpful || 0})
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Reviews;


